import { useMemo } from 'react';

import { getCountries } from '@/utils/getCountries';

export type Country = ReturnType<typeof getCountries>[number];

type RankedCountry = { country: Country; rank: number };

// Lower is better; null means the country does not match at all. "IT" has to land on Italy
// rather than on every name that happens to contain the two letters.
const rankCountry = ({ code, name }: Country, search: string): number | null => {
  const lowerCode = code.toLowerCase();
  const lowerName = name.toLowerCase();

  if (lowerCode === search || lowerName === search) return 0;
  if (lowerName.startsWith(search)) return 1;
  if (lowerCode.startsWith(search)) return 2;
  if (lowerName.split(/[^\p{L}\p{N}]+/u).some((word) => word.startsWith(search))) return 3;
  if (lowerName.includes(search) || lowerCode.includes(search)) return 4;
  return null;
};

/**
 * Countries for a country picker, filtered and ranked by `query`: exact code or name first, then
 * name prefix, code prefix, word prefix and plain substring. An empty query keeps the default
 * order from `getCountries` (United States first).
 */
export const useCountrySearch = (query: string) => {
  const countries = useMemo(() => getCountries(), []);

  const filteredCountries = useMemo(() => {
    const search = query.trim().toLowerCase();
    if (!search) return countries;

    return countries
      .map((country) => ({ country, rank: rankCountry(country, search) }))
      .filter((match): match is RankedCountry => match.rank !== null)
      .sort((a, b) => a.rank - b.rank || a.country.name.localeCompare(b.country.name))
      .map(({ country }) => country);
  }, [countries, query]);

  return { countries, filteredCountries };
};
